import { ParsedUrlQuery } from 'querystring';
import { setCookie } from 'cookies-next';
import { CDP_EVENTS, JOURNEY_STEP } from '../../constants';
import { storeCDPData } from '../../features/cdpDataSlice';
import { hideLoader, showLoader } from '../../features/loaderSlice';
import { post } from '../../lib/utils/axiosFetcher';
import {
  MegaScreenerMetadata,
  MegaScreenerOutcomeParameter,
  MegaScreenerQuestionAnswer,
  MegaScreenerCanonicalFact,
  setMegaScreenerSessionCompleted,
  setMegaScreenerSessionStarted,
} from '../../features/megaScreenerSlice';
import {
  ENROLLMENT_EXPIRY_TIME_FALLBACK,
  getCookieExpiryTime,
} from '../../lib/utils/enrollmentUtil';
import { getQuery } from '../../lib/utils/common';
import * as cdpService from '../../services/CdpService';
import { AppDispatch, store } from '../../store/store';
import { logger } from '../../logger';
import {
  addMegaScreenerPrescreenerMain,
  buildMegaScreenerPrescreenerContext,
  MegaScreenerSitecoreConfig,
  updateMegaScreenerPrescreenerMain,
} from './addMegaScreenerPrescreenerMain';
import {
  MegaScreenerPartialSiteSettingsItem,
  resolveTenantKeyFromPartialSiteSettings,
} from 'src/GraphQL/queries/megaScreenerPartialSiteSettings.query';

export const MEGASCREENER_EVENT_NAMES = [
  'megascreener:screenerMetadata',
  'megascreener:complete',
  'megascreener:error',
  'megascreener:sitecoreEvent',
] as const;

export type MegaScreenerEventName = (typeof MEGASCREENER_EVENT_NAMES)[number];

export type MegaScreenerCompletionConfig = {
  pointOfSale?: string;
  routerQuery?: ParsedUrlQuery;
  retainQueryParamList?: string[];
  removeQueryParamList?: string[];
  resumeEnrollmentExpirationDays?: number;
  navigate?: (
    pathname: string,
    query: Record<string, string | string[] | undefined>
  ) => Promise<boolean>;
};

type SitecoreEventDetail = {
  eventType?: string;
  screenerMetadata?: MegaScreenerMetadata;
  screenerOutcomeParameter?: MegaScreenerOutcomeParameter[];
  qualifiedScreenerResponse?: MegaScreenerQuestionAnswer[];
  allScreenerResponse?: MegaScreenerQuestionAnswer[];
  lastScreenerResponse: MegaScreenerQuestionAnswer[];
  canonicalFacts?: MegaScreenerCanonicalFact[];
};

const MAX_EXPERIENCE_RETRY_COUNT = Number(process.env.RETRY_COUNT ?? 4);

async function fetchMegaScreenerNextStep(pointOfSale?: string) {
  let retryAttempts = 0;
  while (retryAttempts < MAX_EXPERIENCE_RETRY_COUNT) {
    try {
      retryAttempts += 1;
      const experienceId = process.env.NEXT_PUBLIC_CDP_EXPERIENCE_SCREENER_ID;
      return await cdpService.fetchNextPossiblePathAndData(experienceId, false, false, pointOfSale);
    } catch (error) {
      if (retryAttempts >= MAX_EXPERIENCE_RETRY_COUNT) {
        logger.error('[MegaScreener] CDP experience failed after max retries', { error });
        throw new Error('fetchNextSteps failed');
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }
}

async function completeMegaScreenerSessionNavigation(
  dispatch: AppDispatch,
  config: MegaScreenerCompletionConfig
): Promise<void> {
  const data = await fetchMegaScreenerNextStep(config.pointOfSale);
  dispatch(storeCDPData(data));

  if (!data?.nextStepUrl || !config.navigate) {
    if (!data?.nextStepUrl) {
      logger.warn('[MegaScreener] CDP experience returned no nextStepUrl');
    }
    return;
  }

  setCookie('journeyStep', JOURNEY_STEP.SCREENER, {
    expires: getCookieExpiryTime(
      config.resumeEnrollmentExpirationDays || ENROLLMENT_EXPIRY_TIME_FALLBACK
    ),
    secure: true,
  });

  await config.navigate(
    data.nextStepUrl,
    getQuery(config.routerQuery ?? {}, config.retainQueryParamList, config.removeQueryParamList)
  );
  window.scrollTo({ top: 0, behavior: 'auto' });
}

export function createMegaScreenerEventHandler(
  dispatch: AppDispatch,
  sitecoreConfig: MegaScreenerSitecoreConfig,
  completionConfig: MegaScreenerCompletionConfig,
  primaryTASettings: MegaScreenerPartialSiteSettingsItem[] = []
) {
  return (eventName: MegaScreenerEventName, detail: unknown) => {
    switch (eventName) {
      case 'megascreener:screenerMetadata':
        // TODO(megascreener): analytics / metadata API
        break;
      case 'megascreener:complete':
        // TODO(megascreener): prescreener save / CDP update (see AretiHealthScreener)
        break;
      case 'megascreener:error':
        logger.error('[MegaScreener] embed error', { detail });
        break;
      case 'megascreener:sitecoreEvent': {
        const event = detail as SitecoreEventDetail;

        if (event.eventType === 'session_started' && event.screenerMetadata) {
          dispatch(setMegaScreenerSessionStarted({ screenerMetadata: event.screenerMetadata }));

          const responseId = event.screenerMetadata.responseId;
          const prescreenerContext = buildMegaScreenerPrescreenerContext(
            store.getState(),
            sitecoreConfig
          );
          if (prescreenerContext && responseId) {
            const extensionData = {
              tenantKey: sitecoreConfig.tenantKey,
              screenerResponseID: responseId,
              enrollmentId: prescreenerContext.enrollmentId,
              guestRef: prescreenerContext.potentialParticipant_ID,
            };

            cdpService.event(CDP_EVENTS.SCREENER_STARTED, extensionData);
            void addMegaScreenerPrescreenerMain(prescreenerContext, responseId)
              .then(() => {
                setCookie('journeyStep', JOURNEY_STEP.SCREENER, {
                  expires: getCookieExpiryTime(
                    completionConfig?.resumeEnrollmentExpirationDays ||
                      ENROLLMENT_EXPIRY_TIME_FALLBACK
                  ),
                  secure: true,
                });
              })
              .catch((error) => {
                logger.error('[MegaScreener] addPrescreenerMain failed on session_started', {
                  error,
                  responseId,
                });
              });
          } else {
            logger.warn(
              '[MegaScreener] addPrescreenerMain skipped: prescreener context unavailable',
              {
                responseId,
                hasContext: Boolean(prescreenerContext),
              }
            );
          }
        } else if (event.eventType === 'session_completed' && event.screenerMetadata) {
          console.log('MegaScreener session_completed event received' + JSON.stringify(event));

          dispatch(
            setMegaScreenerSessionCompleted({
              screenerMetadata: event.screenerMetadata,
              screenerOutcomeParameter: event.screenerOutcomeParameter ?? [],
              allScreenerResponse: event.allScreenerResponse ?? [],
              lastScreenerResponse: event.lastScreenerResponse ?? [],
              canonicalFacts: event.canonicalFacts ?? [],
            })
          );

          const screenerOutcomeParameter = event.screenerOutcomeParameter ?? [];
          const responseId = event.screenerMetadata.responseId;
          const prescreenerContext = buildMegaScreenerPrescreenerContext(
            store.getState(),
            sitecoreConfig
          );

          let tenantKey: string | undefined;
          if (primaryTASettings.length > 0) {
            const lastScreenedTA = screenerOutcomeParameter?.find(
              (param) => param.key === 'lastScreenedTA'
            )?.value;
            const matchedSettings = resolveTenantKeyFromPartialSiteSettings(
              primaryTASettings,
              lastScreenedTA
            );
            tenantKey = matchedSettings?.tenantKey;
            if (!tenantKey) {
              logger.warn('[MegaScreener] tenant key is not available from partial site settings', {
                lastScreenedTA,
                primaryTASettingsCount: primaryTASettings.length,
                responseId,
              });
            } else {
              const cookieOptions = {
                expires: getCookieExpiryTime(
                  completionConfig?.resumeEnrollmentExpirationDays ||
                    ENROLLMENT_EXPIRY_TIME_FALLBACK
                ),
                secure: true,
              };
              setCookie('tenantKey', tenantKey, cookieOptions);
              if (matchedSettings?.eprName) {
                setCookie('epr-name', matchedSettings.eprName, cookieOptions);
              }

              // Update tenant key in CDP using contacts/UpdateTenantKey
              const updateTenantKeyInCdp = async (resolvedTenantKey: string) => {
                const guestRef = prescreenerContext?.potentialParticipant_ID;
                const enrollmentId = prescreenerContext?.enrollmentId;
                if (!guestRef || !enrollmentId) {
                  logger.warn(
                    '[MegaScreener] skipping CDP tenant key update; missing guestRef or enrollmentId',
                    {
                      guestRef,
                      enrollmentId,
                      responseId,
                    }
                  );
                  return;
                }

                let retryAttempts = 0;
                while (retryAttempts < MAX_EXPERIENCE_RETRY_COUNT) {
                  try {
                    retryAttempts += 1;
                    cdpService.updateTenantKeyInCDP(
                      guestRef,
                      resolvedTenantKey,
                      enrollmentId,
                      prescreenerContext?.guestType || 'visitor'
                    );
                    return;
                  } catch (error) {
                    if (retryAttempts >= MAX_EXPERIENCE_RETRY_COUNT) {
                      logger.error('[MegaScreener] failed to update tenant key in CDP', {
                        error,
                        tenantKey: resolvedTenantKey,
                        guestRef,
                        enrollmentId,
                        responseId,
                      });
                      return;
                    }
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                  }
                }
              };

              // Update tenant key in database using Participant/Add API
              const updateTenantKeyInDatabase = async (resolvedTenantKey: string) => {
                let retryAttempts = 0;
                while (retryAttempts < MAX_EXPERIENCE_RETRY_COUNT) {
                  try {
                    retryAttempts += 1;
                    const { data: response } = await post('/Participant/UpdateTenantKey', {
                      tenantKey: resolvedTenantKey,
                      enrollmentId: prescreenerContext?.enrollmentId,
                    });
                    return response;
                  } catch (error) {
                    if (retryAttempts >= MAX_EXPERIENCE_RETRY_COUNT) {
                      logger.error('[MegaScreener] failed to update tenant key in database', {
                        error,
                        tenantKey: resolvedTenantKey,
                        responseId,
                      });
                      return undefined;
                    }
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                  }
                }
              };

              if (tenantKey) {
                void updateTenantKeyInCdp(tenantKey);
                void updateTenantKeyInDatabase(tenantKey);
              }
            }
          }
          if (prescreenerContext && responseId) {
            const lastScreenedTA = event.screenerOutcomeParameter?.find(
              (param) => param.key === 'lastScreenedTA'
            )?.value;
            const lastScreenedCondition = event.screenerOutcomeParameter?.find(
              (param) => param.key === 'lastScreenedCondition'
            )?.value;
            const extensionData = {
              tenantKey: tenantKey || sitecoreConfig.tenantKey,
              screenerResponseID: responseId,
              enrollmentId: prescreenerContext.enrollmentId,
              lastScreenedCondition: lastScreenedCondition,
              lastScreenedTA: lastScreenedTA,
              guestRef: prescreenerContext.potentialParticipant_ID,
            };

            cdpService.event(CDP_EVENTS.SCREENER_COMPLETED, extensionData);
            dispatch(showLoader());
            void updateMegaScreenerPrescreenerMain(
              prescreenerContext,
              responseId,
              event.screenerOutcomeParameter ?? []
            )
              .then(() => completeMegaScreenerSessionNavigation(dispatch, completionConfig))
              .catch((error) => {
                logger.error('[MegaScreener] session_completed flow failed', {
                  error,
                  responseId,
                });
              })
              .finally(() => {
                dispatch(hideLoader());
              });
          } else {
            logger.warn(
              '[MegaScreener] updatePrescreenerMain skipped: prescreener context unavailable',
              {
                responseId,
                hasContext: Boolean(prescreenerContext),
              }
            );
          }
        }
        break;
      }
      default:
        break;
    }
  };
}

export function subscribeToMegaScreenerEvents(
  onEvent: (eventName: MegaScreenerEventName, detail: unknown) => void
): () => void {
  const handlers = MEGASCREENER_EVENT_NAMES.map((eventName) => {
    const handler = (event: Event) => {
      onEvent(eventName, (event as CustomEvent).detail);
    };
    window.addEventListener(eventName, handler);
    return { eventName, handler };
  });

  return () => {
    handlers.forEach(({ eventName, handler }) => window.removeEventListener(eventName, handler));
  };
}
